import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ChevronRight, MessageCircle, IndianRupee, Calendar, FileText } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20calculated%20my%20GST%20late%20fee%20and%20need%20help";

const RETURN_TYPES = [
  { value: "GSTR-1", label: "GSTR-1" },
  { value: "GSTR-3B", label: "GSTR-3B" },
  { value: "GSTR-9", label: "GSTR-9 (Annual Return)" },
  { value: "NIL", label: "Nil Return" },
];

const formatINR = (num) =>
  Math.round(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const GSTPenaltyCalculatorPage = () => {
  const [returnType, setReturnType] = useState("GSTR-3B");
  const [daysLate, setDaysLate] = useState(10);
  const [taxLiability, setTaxLiability] = useState(50000);

  useCanonical("/gst-penalty-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "GST Late Fee & Penalty Calculator",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "description": "Free GST late fee & penalty calculator for GSTR-1, GSTR-3B, GSTR-9 and Nil returns. Calculates late fee + 18% interest.",
    "url": "https://wintrustsolutions.com/gst-penalty-calculator",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  const results = useMemo(() => {
    const days = Math.max(0, Number(daysLate) || 0);
    const tax = Math.max(0, Number(taxLiability) || 0);
    const isNilByTax = tax === 0;

    let perDay = 0;
    let cap = Infinity;
    let note = "";

    if (returnType === "GSTR-1") {
      perDay = 50;
    } else if (returnType === "GSTR-3B") {
      perDay = isNilByTax ? 20 : 50;
      if (isNilByTax) note = "Nil return rate applied (₹20/day) because tax liability is zero.";
    } else if (returnType === "GSTR-9") {
      perDay = 200;
      cap = tax * 0.0025; // 0.25% of turnover
      note = "Late fee capped at 0.25% of turnover. Enter turnover in the Tax/Turnover field.";
    } else if (returnType === "NIL") {
      perDay = 20;
    }

    let lateFee = perDay * days;
    if (cap !== Infinity && cap > 0 && lateFee > cap) lateFee = cap;

    const cgstLateFee = lateFee / 2;
    const sgstLateFee = lateFee / 2;

    // Interest on tax due (not for GSTR-9 / Nil)
    const interestApplies = returnType !== "GSTR-9" && !isNilByTax && returnType !== "NIL";
    const interest = interestApplies ? (tax * 0.18 * days) / 365 : 0;

    const total = lateFee + interest;

    return { lateFee, cgstLateFee, sgstLateFee, interest, total, perDay, cap, note };
  }, [returnType, daysLate, taxLiability]);

  const taxFieldLabel = returnType === "GSTR-9" ? "Annual Turnover" : "Tax Liability Amount";

  return (
    <>
      <Helmet>
        <title>GST Late Fee & Penalty Calculator — Coimbatore | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Free GST late fee & penalty calculator for GSTR-1, GSTR-3B, GSTR-9, Nil returns. Calculates late fee + 18% interest instantly. Expert help in Coimbatore." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Penalty Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="gst-penalty-h1">
            GST Late Fee & Penalty Calculator — Coimbatore
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Instantly calculate your GST late fee and 18% interest liability under the CGST Act. Supports GSTR-1, GSTR-3B, GSTR-9 and Nil returns. Expert GST regularisation assistance available in Coimbatore.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="bg-slate-50 section-padding" data-testid="gst-penalty-calculator-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Inputs */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-navy mb-6 font-heading">Enter Details</h2>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <FileText size={16} /> Return Type
                </label>
                <select
                  value={returnType}
                  onChange={(e) => setReturnType(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="return-type-select"
                >
                  {RETURN_TYPES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <Calendar size={16} /> Number of Days Late
                </label>
                <input
                  type="number"
                  min={1}
                  value={daysLate}
                  onChange={(e) => setDaysLate(Math.max(1, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="days-late-input"
                />
              </div>

              <div className="mb-2">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <IndianRupee size={16} /> {taxFieldLabel}
                </label>
                <input
                  type="number"
                  min={0}
                  value={taxLiability}
                  onChange={(e) => setTaxLiability(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="tax-liability-input"
                />
                <p className="text-xs text-slate-500 mt-1">Enter 0 for nil returns. For GSTR-9, enter annual turnover.</p>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4" data-testid="gst-penalty-results">
              <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Total Amount Payable</p>
                <p className="text-4xl md:text-5xl font-bold text-gold font-heading" data-testid="total-payable">
                  ₹{formatINR(results.total)}
                </p>
                <p className="text-slate-400 text-xs mt-2">Late fee rate: ₹{results.perDay}/day</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Late Fee Payable</p>
                  <p className="text-xl font-bold text-navy" data-testid="late-fee">₹{formatINR(results.lateFee)}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Interest Payable</p>
                  <p className="text-xl font-bold text-navy" data-testid="interest-payable">₹{formatINR(results.interest)}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <p className="text-navy font-semibold mb-3 text-sm">Late Fee Breakdown</p>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">CGST Portion</span>
                  <span className="text-navy font-semibold text-sm" data-testid="cgst-portion">₹{formatINR(results.cgstLateFee)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 text-sm">SGST Portion</span>
                  <span className="text-navy font-semibold text-sm" data-testid="sgst-portion">₹{formatINR(results.sgstLateFee)}</span>
                </div>
              </div>

              {results.note && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                  {results.note}
                </div>
              )}
            </div>
          </div>

          {/* Amber Alert */}
          <div className="mt-10 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8" data-testid="amber-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={28} />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-base md:text-lg mb-4">
                  Your penalty is accumulating daily. Stop it now — WhatsApp us immediately for GST regularisation support.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Fix My GST Now — WhatsApp Us
                  </a>
                  <Link
                    to="/gst-return-filing"
                    className="text-navy font-semibold hover:text-gold transition-colors flex items-center gap-1"
                    data-testid="link-gst-filing"
                  >
                    View GST Return Filing Services <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            <strong>Disclaimer:</strong> This calculator provides an estimate based on standard GST late fee provisions as per CGST Act. Actual penalties may vary based on CBIC notifications. Consult a GST expert for accurate assessment.
          </p>
        </div>
      </section>
    </>
  );
};

export default GSTPenaltyCalculatorPage;
